'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Activity {
  id: string;
  user: string;
  avatar: string;
  avatarAlt: string;
  action: string;
  signal: string;
  profit?: number;
  timestamp: string;
}

interface CommunityActivityProps {
  activities: Activity[];
}

const CommunityActivity = ({ activities }: CommunityActivityProps) => {
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-muted rounded w-40 mb-4"></div>
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-muted rounded-full"></div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading font-bold text-foreground">Community Activity</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
          <span className="text-xs text-muted-foreground">Live</span>
        </div>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities.map((activity) => (
          <div key={activity.id} className="flex items-start space-x-3 pb-4 border-b border-border last:border-0">
            <div className="relative">
              <AppImage
                src={activity.avatar}
                alt={activity.avatarAlt}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-success rounded-full border-2 border-card"></div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground">
                <span className="font-semibold">{activity.user}</span>
                <span className="text-muted-foreground"> {activity.action} </span>
                <span className="font-semibold text-primary">{activity.signal}</span>
              </p>
              {activity.profit !== undefined && (
                <div className="flex items-center space-x-2 mt-1">
                  <Icon name="ChartBarIcon" size={14} className="text-muted-foreground" />
                  <span className={`text-xs font-semibold ${activity.profit >= 0 ? 'text-success' : 'text-error'}`}>
                    {activity.profit >= 0 ? '+' : ''}{activity.profit.toFixed(2)}% profit
                  </span>
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">{activity.timestamp}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommunityActivity;